import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { Table, Alert, Popconfirm, Tag } from "antd"
import { useHistory, useLocation, Link } from "react-router-dom"
import moment from "moment"

import { formatCurrency } from "../../../lib/utilities"
import {
    discardRun,
    selectPreflightFilter,
    selectRunDetail,
    selectRunGroups,
    selectRunTotals,
    setPreflightFilter,
} from "../clientBatchingSlice"

export default function RunSummary() {
    const dispatch = useDispatch()
    const history = useHistory()
    // RunSummary is shared by Review, Flagged, Pre-Flight and Finalize. ACCEPT SWEEP moves to the
    // next step; on Finalize it would just duplicate the action in the card below.
    const pathname = useLocation().pathname
    const onFinalizePage = pathname.endsWith("/finalize")
    // Review Bills is /runs/:id with nothing after it. Checked as well as the filter, so Update does
    // not flash on Pre-Flight in the moment between applying a finding and navigating to the grid.
    const onReviewPage = /\/runs\/\d+\/?$/.test(pathname)
    const preflightFilter = useSelector(selectPreflightFilter)
    const run = useSelector(selectRunDetail)
    const groups = useSelector(selectRunGroups)
    const totals = useSelector(selectRunTotals)

    if (!run) return null

    const drift = run.drift || {}
    const missing = drift.missing_bill_ids || []
    const alreadyBatched = drift.already_batched_bill_ids || []

    // A/R feedback item 4: after fixing a finding's bills, Update re-runs pre-flight and goes back to
    // it, rather than offering Finalize mid-review. The Pre-Flight page does the re-check on arrival
    // and reports the result (recheck flag).
    const handleUpdate = () => {
        dispatch(setPreflightFilter(null))
        history.push(`/client_batching/runs/${run.id}/preflight`, { recheck: true })
    }

    const handleDiscard = async () => {
        await dispatch(discardRun(run.id))
        history.push("/client_batching")
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                {/* one wrapper: .card-body is a flex row */}
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Run #{run.id}</h2>
                            <div className="badge">{run.included_count}</div>
                        </div>

                        <div className="actions">
                            <div className="table-action-links">
                                {/* A finalized run is history: discard rejects it, and the batches
                                    it created cannot be unmade from here. */}
                                {run.finalized ? (
                                    <Tag color="green">Finalized</Tag>
                                ) : (
                                    <>
                                        {onReviewPage && preflightFilter ? (
                                            <a href="javascript:;" onClick={handleUpdate}>
                                                Update
                                            </a>
                                        ) : (
                                            // Review → Pre-Flight → Finalize: from Review Bills (or
                                            // Flagged) it opens Pre-Flight; from Pre-Flight, Finalize.
                                            !onFinalizePage && (
                                                <Link
                                                    to={`/client_batching/runs/${run.id}/${
                                                        pathname.endsWith("/preflight") ? "finalize" : "preflight"
                                                    }`}
                                                >
                                                    ACCEPT SWEEP
                                                </Link>
                                            )
                                        )}
                                        <Popconfirm
                                            title="Discard this sweep? The clients it holds are released and no bill is changed."
                                            okText="Discard"
                                            cancelText="Keep"
                                            onConfirm={handleDiscard}
                                        >
                                            <a href="javascript:;">DISCARD SWEEP</a>
                                        </Popconfirm>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <p className="p-10">
                        {run.batch_date} · swept by {run.user_name} at {moment(run.created_at).format("h:mm A")} ·{" "}
                        {run.included_count} included, {run.excluded_count} excluded, {run.withheld_count} withheld
                        {run.flagged_count > 0 && (
                            <>
                                {", "}
                                <Link to={`/client_batching/runs/${run.id}/flagged`}>
                                    {run.flagged_count} red-flagged
                                </Link>
                            </>
                        )}
                        {totals ? ` · ${formatCurrency(totals.client_batch_cost_sum)} batch cost` : ""}
                    </p>

                    {/* The sweep marks no bill, so someone else can batch or delete one while the
                        review is open. Finalize re-checks, but the reviewer should see it now. */}
                    {alreadyBatched.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message={`${alreadyBatched.length} bill(s) in this run have since been batched elsewhere and will be skipped at finalize.`}
                        />
                    )}
                    {missing.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message={`${missing.length} bill(s) in this run no longer exist and will be skipped at finalize.`}
                        />
                    )}

                    <Table
                        dataSource={groups}
                        rowKey={(r) => `${r.client_id}-${r.batch_group_id || "none"}`}
                        pagination={false}
                        size="small"
                    >
                        <Table.Column title="Client" key="client_name" dataIndex="client_name" />
                        <Table.Column
                            title="Batch group"
                            key="batch_group_name"
                            render={(text, record) => record.batch_group_name || "No batch group"}
                        />
                        <Table.Column title="Bills" key="bill_count" dataIndex="bill_count" align="right" />
                        <Table.Column
                            title="Current charges"
                            key="current_charges_sum"
                            align="right"
                            render={(text, record) => formatCurrency(record.current_charges_sum)}
                        />
                        <Table.Column
                            title="Client batch cost"
                            key="cost_sum"
                            align="right"
                            render={(text, record) => formatCurrency(record.cost_sum)}
                        />
                    </Table>
                </div>
            </div>
        </div>
    )
}
